/**
 * Chan's Nest Inn – Website configuration
 * Single source: businessInfo (aligned with client app; tagline is website-only).
 */
import logo from '../assets/logo/logo.png';
/** Attraction photos — filenames match `src/assets/attractions/` (spaces OK in quoted paths). */
import imgPollachiRailwayStation from '../assets/attractions/railway station.webp';
import imgAliyarDam from '../assets/attractions/aliyar-dam.avif';
import imgButterflyParkAliyar from '../assets/attractions/Aliyar Butterfly Park.webp';
import imgLoamsViewPoint from '../assets/attractions/loam aaro viewpoint.webp';
import imgTigerValleyViewPoint from '../assets/attractions/Tiger Hill View Point.webp';
import imgWaterfallsPhotoSpot from '../assets/attractions/Waterfalls photo point.webp';
/** No dedicated file yet — reuse a nearby viewpoint photo until you add one. */
import imgKavarkalViewPoint from '../assets/attractions/Tiger Hill View Point.webp';
import imgThalanarViewPoint from '../assets/attractions/Thalanar Valley View Point.webp';
import imgSholayarDam from '../assets/attractions/Sholayar Dam.webp';
import imgSholayarDamViewPoint from '../assets/attractions/Sholayar Dam.webp';
import imgCharpaWaterfalls from '../assets/attractions/Charpa Waterfalls.webp';
import imgVazhachalWaterfalls from '../assets/attractions/Vazhachal Waterfalls.webp';
import imgAthirappillyFalls from '../assets/attractions/Athirappilly Water Falls.webp';
import imgHangingBridgeVazhachal from '../assets/attractions/Vazhachal Bridge.webp';
/** No tea-estates photo in folder yet — placeholder. */
import imgValparaiTeaEstates from '../assets/attractions/Sholayar Dam.webp';
/** No monkey falls photo in folder yet — placeholder. */
import imgMonkeyFalls from '../assets/attractions/Charpa Waterfalls.webp';
import imgBalajiTempleValparai from '../assets/attractions/Balaji Temple.webp';
import imgVellamalaiRiver from '../assets/attractions/Vellamalai Tunnel River.webp';
import imgSirukundraPhotoPoint from '../assets/attractions/Sirukundra.webp';
import imgKarumalaiFallsChurch from '../assets/attractions/Velankanni Matha Church.webp';
/** No koolangal photo in folder yet — placeholder. */
import imgKoolangalRiver from '../assets/attractions/Vellamalai Tunnel River.webp';
import imgChinnakallarRiver from '../assets/attractions/Chinna Kallar Falls.webp';

/** Resort / property carousel — `rel` is for ordering keys & subfolder grouping; no `name` (not shown). */
import gBed from '../assets/gallery/bed.webp';
import gFrontDay from '../assets/gallery/front day.webp';
import gFrontNight from '../assets/gallery/front night.webp';
import gGarden from '../assets/gallery/garden.webp';
import gGarden2 from '../assets/gallery/garden2.webp';
import gGarden3 from '../assets/gallery/garden3.webp';
import gGarden4 from '../assets/gallery/garden4.webp';
import gGarden5 from '../assets/gallery/garden5.webp';
import gKitchen from '../assets/gallery/kitchen.webp';
import gR1Cupboard from '../assets/gallery/r1 cupboard.webp';
import gR2Cupboard from '../assets/gallery/r2 cupboard.webp';
import gRoom1CupboardTv from '../assets/gallery/room 1 cupboard and tv.webp';
import gRoom1Full from '../assets/gallery/room 1 full.webp';
import gSitout from '../assets/gallery/sitout.webp';
import gSitout2 from '../assets/gallery/sitout2.webp';
import gTable from '../assets/gallery/table.webp';
import gToilet from '../assets/gallery/toilet.webp';

import aElephant from '../assets/animals/elephant.webp';
import aHandbill from '../assets/animals/handbill.webp';
import aLeopard from '../assets/animals/leopard.webp';
import aMacaque from '../assets/animals/lion tailed macaque.webp';
import aSlothBear from '../assets/animals/sloth bear.webp';
import aTahr from '../assets/animals/tahr.webp';

const isLocalEnv = process.env.NODE_ENV === 'development';

export const API_BASE = isLocalEnv
  ? 'http://localhost:5000'
  : (process.env.REACT_APP_API || 'https://chans-nest-inn-81236497739.asia-south2.run.app');

export const businessInfo = {
  name: "Chan's Nest Inn",
  logo,
  phones: ['9488570704', '9894039977'],
  address: '17/665-B EB Road, Sholayar Dam (P.O), Sholayar Dam, Valparai - 642125',
  upiId: '',
  thankYouMessage: 'Thank you for your visit! We hope to see you again!',
  email: 'info@chansnest.in',
  tagline: 'Your peaceful retreat in Sholayar',
  mapEmbedUrl:
    'https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2933.8819745842457!2d76.88021479999999!3d10.3039899!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3b078b1bd35cd527%3A0xe639f2c851962c2d!2sChan%27s%20Nest%20Inn!5e1!3m2!1sen!2sin!4v1774176796362!5m2!1sen!2sin',
  /** Footer social icons — instagram, threads, facebook, youtube, googleMaps (empty string hides). */
  socialLinks: {
    instagram: 'https://www.instagram.com/chansnest.in',
    threads: 'https://www.threads.net/@chansnest.in',
    facebook: 'https://www.facebook.com/chansnest.in',
    youtube: 'https://www.youtube.com/@ChansNestInn',
    googleMaps:
      'https://maps.app.goo.gl/VbvALbRG9n2DuPxv5',
  },
};

/**
 * Homepage YouTube hero — landscape on desktop, portrait / Shorts on mobile.
 * Paste a video id or full URL (`watch`, `embed`, `youtu.be`, `shorts`).
 */
export const heroYouTubeVideos = {
  horizontal: 'LmTwASdak8o',
  vertical: 'LmTwASdak8o',
};

/**
 * Places master list (website-only).
 * - Use `id` everywhere (tour plan + cards).
 * - `showInAttractions` controls whether it appears in the cards carousel.
 */
export const places = [
  {
    id: 'pollachi-railway-station',
    name: 'Pollachi Railway Station',
    imageUrl: imgPollachiRailwayStation,
    mapQuery: 'Pollachi Railway Station',
    distanceKm: 80,
    popularity: 60,
    showInAttractions: false,
  },
  {
    id: 'aliyar-dam',
    name: 'Aliyar Dam',
    imageUrl: imgAliyarDam,
    mapQuery: 'Aliyar Dam',
    distanceKm: 57,
    popularity: 80,
    showInAttractions: true,
  },
  {
    id: 'butterfly-park',
    name: 'Butterfly Park',
    imageUrl: imgButterflyParkAliyar,
    mapQuery: 'Aliyar Butterfly Park',
    distanceKm: 55,
    popularity: 70,
    showInAttractions: true,
  },
  {
    id: 'loams-view-point',
    name: 'Loam’s View Point',
    imageUrl: imgLoamsViewPoint,
    mapQuery: "Loam's View Point Valparai",
    distanceKm: 45,
    popularity: 88,
    showInAttractions: true,
  },
  {
    id: 'tiger-valley-view-point',
    name: 'Tiger Valley View Point',
    imageUrl: imgTigerValleyViewPoint,
    mapQuery: 'Tiger Hill View Point Valparai',
    distanceKm: 37,
    popularity: 87,
    showInAttractions: true,
  },
  {
    id: 'waterfalls-photo-spot',
    name: 'Waterfalls Photo Spot',
    imageUrl: imgWaterfallsPhotoSpot,
    mapQuery: 'Valparai WWaterfall tea estate view point',
    distanceKm: 47,
    popularity: 65,
    showInAttractions: true,
  },
  {
    id: 'kavarkal-view-point',
    name: 'Kavarkal View Point',
    imageUrl: imgKavarkalViewPoint,
    mapQuery: 'Kavarkal mist spreading zone Valparai',
    distanceKm: 27,
    popularity: 82,
    showInAttractions: true,
  },
  {
    id: 'thalanar-view-point',
    name: 'Thalanar View Point',
    imageUrl: imgThalanarViewPoint,
    mapQuery: 'Thalanar Valley View Point',
    distanceKm: 35,
    popularity: 79,
    showInAttractions: true,
  },
  {
    id: 'sholayar-dam',
    name: 'Sholayar Dam',
    imageUrl: imgSholayarDam,
    mapQuery: 'Sholayar Dam Bridge',
    distanceKm: 2,
    popularity: 96,
    showInAttractions: true,
  },
  {
    id: 'sholayar-dam-view-point',
    name: 'Sholayar Dam View Point',
    imageUrl: imgSholayarDamViewPoint,
    mapQuery: 'Sholayar Dam View Point',
    distanceKm: 1,
    popularity: 100,
    showInAttractions: true,
  },
  {
    id: 'charpa-waterfalls',
    name: 'Charpa Waterfalls',
    imageUrl: imgCharpaWaterfalls,
    mapQuery: 'Charpa Waterfalls',
    distanceKm: 51,
    popularity: 92,
    showInAttractions: true,
  },
  {
    id: 'vazhachal-waterfalls',
    name: 'Vazhachal Waterfalls',
    imageUrl: imgVazhachalWaterfalls,
    mapQuery: 'Vazhachal Waterfalls',
    distanceKm: 50,
    popularity: 90,
    showInAttractions: true,
  },
  {
    id: 'athirappilly-falls',
    name: 'Athirappilly Falls',
    imageUrl: imgAthirappillyFalls,
    mapQuery: 'Athirappilly Falls',
    distanceKm: 56,
    popularity: 98,
    showInAttractions: true,
  },
  {
    id: 'hanging-bridge',
    name: 'Hanging Bridge',
    imageUrl: imgHangingBridgeVazhachal,
    mapQuery: 'Vazhachal Hanging Bridge',
    distanceKm: 48,
    popularity: 68,
    showInAttractions: true,
  },
  {
    id: 'valparai-tea-estates',
    name: 'Valparai Tea Estates',
    imageUrl: imgValparaiTeaEstates,
    mapQuery: 'Valparai Tea Estate View Point',
    distanceKm: 22,
    popularity: 95,
    showInAttractions: true,
  },
  {
    id: 'monkey-falls',
    name: 'Monkey Falls',
    imageUrl: imgMonkeyFalls,
    mapQuery: 'Monkey Falls Tamil Nadu',
    distanceKm: 52,
    popularity: 78,
    showInAttractions: true,
  },
  {
    id: 'balaji-temple',
    name: 'Balaji Temple',
    imageUrl: imgBalajiTempleValparai,
    mapQuery: 'Balaji Temple Valparai',
    distanceKm: 31,
    popularity: 75,
    showInAttractions: true,
  },
  {
    id: 'vellamalai-river',
    name: 'Vellamalai River',
    imageUrl: imgVellamalaiRiver,
    mapQuery: 'Vellamalai River Valparai',
    distanceKm: 25,
    popularity: 62,
    showInAttractions: false,
  },
  {
    id: 'sirukundra-photo-point',
    name: 'Sirukundra Photo Point',
    imageUrl: imgSirukundraPhotoPoint,
    mapQuery: 'Koozhankal River Valparai',
    distanceKm: 27,
    popularity: 61,
    showInAttractions: false,
  },
  {
    id: 'karumalai-falls-church',
    name: 'Karumalai Falls & Church',
    imageUrl: imgKarumalaiFallsChurch,
    mapQuery: 'Karumalai Valparai Church',
    distanceKm: 44,
    popularity: 73,
    showInAttractions: true,
  },
  {
    id: 'koolangal-river',
    name: 'Koozhankal River',
    imageUrl: imgKoolangalRiver,
    mapQuery: 'Koozhankal River Valparai',
    distanceKm: 29,
    popularity: 59,
    showInAttractions: false,
  },
  {
    id: 'chinnakallar-river',
    name: 'Chinna Kallar Falls',
    imageUrl: imgChinnakallarRiver,
    mapQuery: 'Chinna Kallar Falls Valparai',
    distanceKm: 37,
    popularity: 77,
    showInAttractions: true,
  },
];

/** Suggested tour plan (website-only). */
export const tourItinerary = {
  title: '3 Days Valparai & Athirapalli Tour Itinerary',
  days: [
    {
      day: 1,
      title: 'Arrival & Valparai Entry',
      pickupDrop: 'Pick up from Pollachi Railway Station · Evening drop at room',
      stops: [
        { placeId: 'pollachi-railway-station', label: 'Pick up' },
        { placeId: 'aliyar-dam' },
        { placeId: 'butterfly-park' },
        { placeId: 'loams-view-point' },
        { placeId: 'tiger-valley-view-point' },
        { placeId: 'waterfalls-photo-spot' },
        { placeId: 'kavarkal-view-point' },
        { placeId: 'thalanar-view-point' },
      ],
    },
    {
      day: 2,
      title: 'Athirapalli Waterfalls Route',
      pickupDrop: 'Evening return & drop at room',
      stops: [
        { placeId: 'sholayar-dam' },
        { placeId: 'charpa-waterfalls' },
        { placeId: 'vazhachal-waterfalls' },
        { placeId: 'athirappilly-falls' },
        { placeId: 'hanging-bridge' },
      ],
    },
    {
      day: 3,
      title: 'Valparai Local Sightseeing & Departure',
      pickupDrop: 'Evening drop at Pollachi Railway Station',
      stops: [
        { placeId: 'balaji-temple' },
        { placeId: 'vellamalai-river' },
        { placeId: 'sirukundra-photo-point' },
        { placeId: 'karumalai-falls-church' },
        { placeId: 'koolangal-river' },
        { placeId: 'chinnakallar-river' },
        { placeId: 'pollachi-railway-station', label: 'Drop' },
      ],
    },
  ],
};

/**
 * Gallery ordering (optional).
 * Add filename fragments in the exact order you want, e.g.:
 * ['lobby', 'deluxe', 'suite', 'bathroom', 'food', 'exterior']
 */
export const galleryOrder = [];

/**
 * Optional ordering for `assets/animals` (filename fragments, same as galleryOrder).
 */
export const animalGalleryOrder = [];

/** Property photos for `GallerySection` (order = array order; subfolders via `rel` prefix `folder/file`). */
export const resortGalleryImages = [
  { rel: 'bed.webp', url: gBed },
  { rel: 'front day.webp', url: gFrontDay },
  { rel: 'front night.webp', url: gFrontNight },
  { rel: 'garden.webp', url: gGarden },
  { rel: 'garden2.webp', url: gGarden2 },
  { rel: 'garden3.webp', url: gGarden3 },
  { rel: 'garden4.webp', url: gGarden4 },
  { rel: 'garden5.webp', url: gGarden5 },
  { rel: 'kitchen.webp', url: gKitchen },
  { rel: 'r1 cupboard.webp', url: gR1Cupboard },
  { rel: 'r2 cupboard.webp', url: gR2Cupboard },
  { rel: 'room 1 cupboard and tv.webp', url: gRoom1CupboardTv },
  { rel: 'room 1 full.webp', url: gRoom1Full },
  { rel: 'sitout.webp', url: gSitout },
  { rel: 'sitout2.webp', url: gSitout2 },
  { rel: 'table.webp', url: gTable },
  { rel: 'toilet.webp', url: gToilet },
];

/**
 * Guest reviews for the homepage carousel.
 * `rating` is 1–5; edit or add rows as you collect real feedback.
 */
export const guestReviews = [
  {
    customerName: 'SEKAR MOHAN',
    rating: 5,
    review:
      'Rooms Are Excellent ,Good Heart owner , Especially Food awesome taste , Everything is Semaaaa , Must book',
  },
  {
    customerName: 'Chan Core',
    rating: 5,
    review:
      'Majaaa stay with an ownership service',
  },
  {
    customerName: 'TAMIL JEEVAN S S',
    rating: 5,
    review:
      'Excellent stay at Chan’s Nest Inn. The rooms were clean, comfortable, and well-maintained. Staff were polite, responsive, and made the experience smooth from check-in to check-out. The atmosphere was peaceful, the service was professional, and the overall value for the price was outstanding. A reliable and comfortable place to stay. Highly recommended for both short and extended visits.',
  },
  {
    customerName: 'Rameshkumar Varadharajan',
    rating: 5,
    review:
      'We had a fantastic stay at Chan’s Nest Valparai. A huge thank you to the owner and staff for the warm welcome and excellent hospitality. The rooms are beautifully maintained and very comfortable. ​A special shout-out to the chef—both dinner and breakfast - were outstanding! Our only minor suggestion for the summer months. We will definitely be recommending this resort to our friends and relatives. The food was 10/10. Thank you, Satish bro. The place is so well-maintained. Highly recommend it for our next group getaway!"💜💜💕',
  },
  {
    customerName: 'Syed Nazeef',
    rating: 5,
    review:
    'The cleanliness is very crazy over here and staff contribution to everything very good mr Sathish were there to contribute in each step'
  },
  {
    customerName: 'rohan chaudry',
    rating: 5,
    review:
      'Nice stay with proper welcoming and food facilities and an unmemorable stay with my family and staffs were more kind and more worth it for money',
  },
  {
    customerName: 'Dharshini R',
    rating: 5,
    review:
      'Friendly nd caring staff, great service, and a pleasant stay...',
  },
];

/** Wildlife photos — `name` is shown on the carousel; resort gallery does not use names. */
export const animalGalleryImages = [
  { rel: 'elephant.webp', url: aElephant, name: 'Elephant' },
  { rel: 'handbill.webp', url: aHandbill, name: 'Malabar Whistling Thrush' },
  { rel: 'leopard.webp', url: aLeopard, name: 'Indian leopard' },
  { rel: 'lion tailed macaque.webp', url: aMacaque, name: 'Lion-Tailed Macaque' },
  { rel: 'sloth bear.webp', url: aSlothBear, name: 'Sloth Bear' },
  { rel: 'tahr.webp', url: aTahr, name: 'Tahr' },
];

export const devInfo = {
  name: 'Kuruvi Tech',
  location: 'Pollachi, Aruppukottai',
  contact: 'Phone / WhatsApp: 8778927733',
  whatsapp: '918778927733',
  email: 'info@kuruvi.club',
  website: 'https://tech.kuruvi.club',
};

export const firebaseConfig = {
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
  storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
  appId: process.env.REACT_APP_FIREBASE_APP_ID,
};

export default businessInfo;
